/**
 * MapFilterPanel — painel lateral do módulo Mapa: fonte, filtros e cards de métrica.
 *
 * - Fonte: Google Ads, SEO, Meta Ads ou Todas (muda o tipo de tráfego).
 * - Filtros: dimensões dos acessos (origem, mídia, campanha, página, referer,
 *   dispositivo, palavra-chave), cada um com lista pesquisável e seleção múltipla.
 *   As opções vêm de /api/mapa/options respeitando os outros filtros.
 * - Métricas: cards com o total já filtrado; clicar troca o que o mapa pinta.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from './icons.jsx'
import MetricCards from './MetricCards.jsx'
import { getMapaOptions } from '../db/api.js'
import {
  MAPA_SOURCES,
  MAPA_FILTERS,
  MAPA_METRICS,
  MAPA_EMPTY_FILTERS,
  ORIGIN_LOGOS,
  ORIGINS_HIDDEN_IN_LIST,
  countActiveFilters,
  originLabel,
} from '../lib/mapaFilters.js'

const nf = new Intl.NumberFormat('pt-BR')

function SourceIcon({ source }) {
  return source.logo
    ? <img className="mf-src-logo" src={source.logo} alt="" width={18} height={18} />
    : <Icon n={source.icon || 'link'} s={16} />
}

const originItem = (value) => ({
  key: value,
  label: originLabel(value),
  logo: ORIGIN_LOGOS[value],
  icon: 'link',
})

// Dropdown da fonte: grupos fixos no topo (Google Ads, SEO, Meta Ads, Todas) e,
// abaixo, as origens (utm_source) que existem nos dados, com contagem. Origem nova
// aparece sozinha. Fecha ao clicar fora ou com Esc.
function SourceDropdown({ source, origin, onGroup, onOrigin, loadOrigins }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [origins, setOrigins] = useState([])
  const [loading, setLoading] = useState(false)
  const wrapRef = useRef(null)
  const requestRef = useRef(0)
  const group = MAPA_SOURCES.find((s) => s.key === source) || MAPA_SOURCES[0]
  const current = origin ? originItem(origin) : group

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const id = ++requestRef.current
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const rows = await loadOrigins(query)
        if (id === requestRef.current) setOrigins(rows)
      } catch {
        if (id === requestRef.current) setOrigins([])
      } finally {
        if (id === requestRef.current) setLoading(false)
      }
    }, query ? 250 : 0)
    return () => { clearTimeout(timer); requestRef.current += 1 }
  }, [open, query, loadOrigins])

  const pick = (fn) => {
    setOpen(false)
    setQuery('')
    fn()
  }

  return (
    <div className="mf-src" ref={wrapRef}>
      <button
        type="button"
        className="mf-src-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Fonte: ${current.label}`}
        onClick={() => setOpen((v) => !v)}
      >
        <SourceIcon source={current} />
        <span className="mf-src-name">{current.label}</span>
        <Icon n="chevron_d" s={12} />
      </button>
      {open && (
        <div className="mf-src-pop" role="listbox" aria-label="Fonte dos dados">
          {MAPA_SOURCES.map((s) => (
            <button
              key={s.key}
              type="button"
              role="option"
              aria-selected={!origin && s.key === source}
              className={`cf-row mf-src-row${!origin && s.key === source ? ' is-active' : ''}`}
              onClick={() => pick(() => onGroup(s.key))}
            >
              <SourceIcon source={s} />
              <span className="cf-row-name">{s.label}</span>
              {!origin && s.key === source && <Icon n="check" s={13} />}
            </button>
          ))}

          <div className="mf-src-sep">Origens encontradas</div>
          <div className="cf-search mf-src-search">
            <Icon n="search" s={13} />
            <input
              type="text"
              className="cf-search-input"
              placeholder="Buscar origem..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar origem"
            />
          </div>
          <div className="mf-src-origins">
            {loading && origins.length === 0 && <div className="cf-empty">Carregando...</div>}
            {!loading && origins.length === 0 && <div className="cf-empty">Nenhuma origem encontrada</div>}
            {origins.map((o) => {
              const item = originItem(o.value)
              const active = origin === o.value
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  title={o.value}
                  className={`cf-row mf-src-row${active ? ' is-active' : ''}`}
                  onClick={() => pick(() => onOrigin(o.value))}
                >
                  <SourceIcon source={item} />
                  <span className="cf-row-name">{item.label}</span>
                  <span className="cf-row-source">{nf.format(o.count)}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function FilterAccordion({ def, selected, open, onToggle, onChange, loadOptions }) {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const requestRef = useRef(0)

  useEffect(() => {
    if (!open) return undefined
    const id = ++requestRef.current
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const rows = await loadOptions(def.key, query)
        if (id === requestRef.current) setOptions(rows)
      } catch {
        if (id === requestRef.current) setOptions([])
      } finally {
        if (id === requestRef.current) setLoading(false)
      }
    }, query ? 250 : 0)
    return () => { clearTimeout(timer); requestRef.current += 1 }
  }, [open, query, def.key, loadOptions])

  const toggle = (value) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])

  // Valores já marcados que não estão na lista atual (busca ou limite) continuam visíveis.
  const known = new Set(options.map((o) => o.value))
  const extra = selected.filter((v) => !known.has(v)).map((v) => ({ value: v, label: v, count: null }))
  const rows = [...extra, ...options]

  return (
    <div className={`mf-acc${open ? ' is-open' : ''}`}>
      <button type="button" className="mf-acc-head" onClick={onToggle} aria-expanded={open}>
        <span className="mf-acc-label">{def.label}</span>
        {selected.length > 0 && <span className="mf-badge">{selected.length}</span>}
        <Icon n="chevron_d" s={12} />
      </button>

      {open && (
        <div className="mf-acc-body">
          <div className="cf-search">
            <Icon n="search" s={13} />
            <input
              type="text"
              className="cf-search-input"
              placeholder={def.placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button className="cf-search-clear" onClick={() => setQuery('')} type="button" aria-label="Limpar busca">
                <Icon n="x" s={12} />
              </button>
            )}
          </div>
          <div className="mf-list">
            {loading && rows.length === 0 && <div className="cf-empty">Carregando...</div>}
            {!loading && rows.length === 0 && <div className="cf-empty">Nenhum valor encontrado</div>}
            {rows.map((o) => {
              const checked = selected.includes(o.value)
              return (
                <button key={o.value} type="button" className="cf-row" onClick={() => toggle(o.value)}>
                  <span className={`cf-check ${checked ? 'cf-check-on' : ''}`}>
                    {checked && <Icon n="check" s={11} />}
                  </span>
                  <span className="cf-row-name" title={o.label}>{o.label}</span>
                  {o.count != null && <span className="cf-row-source">{nf.format(o.count)}</span>}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default function MapFilterPanel({
  source,
  onSourceChange,
  onOriginChange,
  filters,
  onFiltersChange,
  metric,
  onMetricChange,
  totals,
  disabled = false,
  startDate,
  endDate,
  clientId,
}) {
  const [openKey, setOpenKey] = useState(null)
  const active = countActiveFilters(filters)
  const filtersKey = JSON.stringify(filters)
  const origin = filters.origem?.[0] || null

  // Opções de uma dimensão: dependem da fonte, do período, do cliente e dos
  // outros filtros (a própria dimensão é ignorada no backend).
  const loadOptions = useCallback(
    async (dimension, q) => {
      const res = await getMapaOptions({
        dimension, startDate, endDate, source, q, clientId, filters: JSON.parse(filtersKey),
      })
      return res?.options || []
    },
    [startDate, endDate, clientId, source, filtersKey],
  )

  // Origens existentes (todas as fontes, sem outros filtros), mais acessadas primeiro.
  const loadOrigins = useCallback(
    async (q) => {
      const res = await getMapaOptions({
        dimension: 'origem', startDate, endDate, source: 'all', q, clientId, filters: {},
      })
      return (res?.options || []).filter((o) => !ORIGINS_HIDDEN_IN_LIST.includes(o.value))
    },
    [startDate, endDate, clientId],
  )

  const sourceHint = origin
    ? `Acessos com origem "${origin}" (utm_source), pagos e orgânicos.`
    : MAPA_SOURCES.find((s) => s.key === source)?.hint

  return (
    <aside className="mf-panel" aria-label="Filtros do mapa">
      <section className="mf-block">
        <h3 className="mf-title">Fonte</h3>
        <SourceDropdown
          source={source}
          origin={origin}
          onGroup={onSourceChange}
          onOrigin={onOriginChange}
          loadOrigins={loadOrigins}
        />
        <p className="mf-hint">{sourceHint}</p>
      </section>

      <section className="mf-block">
        <div className="mf-title-row">
          <h3 className="mf-title">Filtros</h3>
          {active > 0 && (
            <button type="button" className="cf-btn-text" onClick={() => onFiltersChange({ ...MAPA_EMPTY_FILTERS, origem: filters.origem || [] })}>
              Limpar ({active})
            </button>
          )}
        </div>
        {MAPA_FILTERS.map((def) => (
          <FilterAccordion
            key={def.key}
            def={def}
            selected={filters[def.key] || []}
            open={openKey === def.key}
            onToggle={() => setOpenKey(openKey === def.key ? null : def.key)}
            onChange={(values) => onFiltersChange({ ...filters, [def.key]: values })}
            loadOptions={loadOptions}
          />
        ))}
      </section>

      <section className="mf-block">
        <h3 className="mf-title">Métricas</h3>
        <MetricCards
          inline
          list
          active={metric}
          onChange={onMetricChange}
          values={totals}
          items={MAPA_METRICS}
          disabled={disabled}
        />
      </section>
    </aside>
  )
}
